import { Option } from '@sapphire/result';

import {
  SURVEY_CLASSIFICATION_KIND,
  SURVEY_CLASSIFICATION_ROLE,
} from '../constants/survey-classification.constants';
import { classifySurvey } from '../survey-classifier/survey-classifier';
import { ChildSurveyFilter } from '../models/child-survey.model';
import { Survey } from '../models/survey.model';

// Boolean checks. For branching/match flows, use classifySurvey directly.
export class SurveyKind {
  public static isStandard(survey: Survey): boolean {
    return classifySurvey(survey).kind === SURVEY_CLASSIFICATION_KIND.STANDARD;
  }

  public static isTraditionalMc(survey: Survey): boolean {
    return classifySurvey(survey).kind === SURVEY_CLASSIFICATION_KIND.MC;
  }

  public static isGroupMc(survey: Survey): boolean {
    return classifySurvey(survey).kind === SURVEY_CLASSIFICATION_KIND.GROUP_MC;
  }

  public static isGroupMcOrTracker(survey: Survey): boolean {
    return this.isGroupMc(survey) || this.isTracker(survey);
  }

  public static isGroupMcParent(survey: Survey): boolean {
    const classification = classifySurvey(survey);
    return (
      classification.kind === SURVEY_CLASSIFICATION_KIND.GROUP_MC &&
      classification.role === SURVEY_CLASSIFICATION_ROLE.PARENT
    );
  }

  public static isGroupMcChild(survey: Survey): boolean {
    const classification = classifySurvey(survey);
    return (
      classification.kind === SURVEY_CLASSIFICATION_KIND.GROUP_MC &&
      classification.role === SURVEY_CLASSIFICATION_ROLE.CHILD
    );
  }

  public static isTracker(survey: Survey): boolean {
    return classifySurvey(survey).kind === SURVEY_CLASSIFICATION_KIND.TRACKER;
  }

  public static isTrackerParent(survey: Survey): boolean {
    const classification = classifySurvey(survey);
    return (
      classification.kind === SURVEY_CLASSIFICATION_KIND.TRACKER &&
      classification.role === SURVEY_CLASSIFICATION_ROLE.PARENT
    );
  }

  public static isTrackerChild(survey: Survey): boolean {
    const classification = classifySurvey(survey);
    return (
      classification.kind === SURVEY_CLASSIFICATION_KIND.TRACKER &&
      classification.role === SURVEY_CLASSIFICATION_ROLE.CHILD
    );
  }

  public static isGrouped(survey: Survey): boolean {
    return classifySurvey(survey).kind === SURVEY_CLASSIFICATION_KIND.GROUPED;
  }

  public static isGroupedParent(survey: Survey): boolean {
    const classification = classifySurvey(survey);
    return (
      classification.kind === SURVEY_CLASSIFICATION_KIND.GROUPED &&
      classification.role === SURVEY_CLASSIFICATION_ROLE.PARENT
    );
  }

  public static isGroupedChild(survey: Survey): boolean {
    const classification = classifySurvey(survey);
    return (
      classification.kind === SURVEY_CLASSIFICATION_KIND.GROUPED &&
      classification.role === SURVEY_CLASSIFICATION_ROLE.CHILD
    );
  }

  public static isProject(survey: Survey): boolean {
    return [
      this.isGroupMc(survey),
      this.isGrouped(survey),
      this.isTracker(survey),
    ].some(Boolean);
  }

  public static isMultiSurvey(survey: Survey): boolean {
    return this.isTraditionalMc(survey) || this.isProject(survey);
  }

  public static isProjectParent(survey: Survey): boolean {
    return [
      this.isGroupMcParent(survey),
      this.isGroupedParent(survey),
      this.isTrackerParent(survey),
    ].some(Boolean);
  }

  public static childFilter(survey: Survey): Option<ChildSurveyFilter> {
    if (this.isTraditionalMc(survey) || this.isGroupMcParent(survey)) {
      return Option.from(survey.mcUniqueId).map<ChildSurveyFilter>(
        (mcUniqueId) => ({ mcUniqueId }),
      );
    }

    if (this.isProjectParent(survey)) {
      return Option.from(survey.id).map<ChildSurveyFilter>((projectId) => ({
        projectId,
      }));
    }

    return Option.none;
  }
}
